import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRightOutlined } from "@ant-design/icons";
import { Button, Empty, Input, Select, Table, message } from "antd";
import { userMessageForError } from "../../utils/apiError";
import { iaRequest } from "./iaApi";

const { TextArea } = Input;

const DEPARTMENT_OPTIONS = [
  "IT Security",
  "IT Operations",
  "HR",
  "Legal & Compliance",
  "Finance",
  "Administration",
  "Engineering",
  "Product",
  "Customer Support",
  "Management",
].map((d) => ({ value: d, label: d }));

const TYPE_OPTIONS = [
  { value: "Conformity", label: "Conformity" },
  { value: "NC Major", label: "NC Major" },
  { value: "NC Minor", label: "NC Minor" },
  { value: "Observation", label: "Observation" },
];

const TYPE_COLORS = {
  Conformity: "text-[color:var(--color-ok-fg)] bg-[color:var(--color-ok-bg)] border-[color:var(--color-ok-line)]",
  "NC Major": "text-[color:var(--color-danger-fg)] bg-[color:var(--color-danger-bg)] border-[color:var(--color-danger-line)]",
  "NC Minor": "text-[color:var(--color-warn-fg)] bg-[color:var(--color-warn-bg)] border-[color:var(--color-warn-line)]",
  Observation: "text-[color:var(--color-accent)] bg-[color:var(--color-info-bg)] border-[color:var(--color-info-line)]",
};

/**
 * Audit Findings sub-tab — table of findings from questionnaire documents.
 */
const NON_CONFORMITY_TYPES = new Set(["NC Major", "NC Minor", "Observation"]);

export function AuditFindingsSubTab({
  projectId,
  auditFindings,
  canEdit,
  getApiToken,
  onPackageUpdated,
  onSwitchTab,
}) {
  const [localRows, setLocalRows] = useState(auditFindings?.rows || []);
  const lastExternalRef = React.useRef(auditFindings?.rows);

  useEffect(() => {
    if (auditFindings?.rows && auditFindings.rows !== lastExternalRef.current) {
      if (auditFindings.rows.length !== localRows.length) {
        setLocalRows(auditFindings.rows);
      }
      lastExternalRef.current = auditFindings.rows;
    }
  }, [auditFindings?.rows]);

  // Bumped when a save is refused so the uncontrolled inputs remount with the restored values.
  const [revision, setRevision] = useState(0);

  const patchFinding = useCallback(async (findingId, patch) => {
    if (!projectId) return;
    const before = localRows.find((r) => r.id === findingId);
    setLocalRows((prev) =>
      prev.map((r) => (r.id === findingId ? { ...r, ...patch } : r)),
    );
    try {
      await iaRequest(
        getApiToken,
        `/api/v1/projects/${projectId}/internal-audit/findings/${encodeURIComponent(findingId)}`,
        { method: "PATCH", body: patch },
      );
    } catch (e) {
      if (before) {
        const restore = Object.fromEntries(Object.keys(patch).map((k) => [k, before[k]]));
        setLocalRows((prev) => prev.map((r) => (r.id === findingId ? { ...r, ...restore } : r)));
        setRevision((n) => n + 1);
      }
      message.error(userMessageForError(e, "Could not save finding."));
    }
  }, [projectId, getApiToken, localRows]);

  const allTypesSet = useMemo(
    () => localRows.length > 0 && localRows.every((r) => r.type),
    [localRows],
  );

  const nonConformityReady = useMemo(
    () => localRows
      .filter((r) => NON_CONFORMITY_TYPES.has(r.type))
      .every((r) => (r.findings || "").trim().length > 0),
    [localRows],
  );

  const hasNonConformity = useMemo(
    () => localRows.some((r) => NON_CONFORMITY_TYPES.has(r.type)),
    [localRows],
  );

  const canProceed = allTypesSet && hasNonConformity && nonConformityReady;

  const [proceedLoading, setProceedLoading] = useState(false);
  const handleProceedToCorrectiveActions = useCallback(async () => {
    if (!projectId || proceedLoading) return;
    setProceedLoading(true);
    try {
      const data = await iaRequest(
        getApiToken,
        `/api/v1/projects/${projectId}/internal-audit/generate-corrective-actions`,
        { method: "POST" },
      );
      if (data?.internalAuditPackage && onPackageUpdated) {
        onPackageUpdated(data.internalAuditPackage);
      }
      if (onSwitchTab) onSwitchTab("corrective_actions");
    } catch (e) {
      message.error(userMessageForError(e, "Could not generate corrective actions."));
    } finally {
      setProceedLoading(false);
    }
  }, [projectId, proceedLoading, getApiToken, onPackageUpdated, onSwitchTab]);

  const columns = useMemo(() => [
    {
      title: "Finding",
      dataIndex: "id",
      key: "id",
      width: 120,
      render: (v) => <span className="font-mono text-xs font-bold text-[color:var(--color-text-muted-dark)]">{v}</span>,
    },
    {
      title: "Clause",
      dataIndex: "clause",
      key: "clause",
      width: 90,
      render: (v) => <span className="text-xs font-semibold text-[color:var(--color-accent)]">{v}</span>,
    },
    {
      title: "Requirement",
      dataIndex: "requirement",
      key: "requirement",
      ellipsis: true,
    },
    {
      title: "Evidence",
      dataIndex: "evidence",
      key: "evidence",
      ellipsis: true,
    },
    {
      title: "Findings",
      key: "findings",
      width: 200,
      render: (_, row) => {
        if (!canEdit) return <span className="text-xs text-[color:var(--color-text-muted)]">{row.findings || "-"}</span>;
        return (
          <TextArea
            key={`${row.id}-${revision}`}
            defaultValue={row.findings || ""}
            rows={2}
            className="text-xs"
            placeholder="Enter findings..."
            onBlur={(e) => {
              const val = e.target.value.trim();
              if (val !== (row.findings || "")) patchFinding(row.id, { findings: val });
            }}
          />
        );
      },
    },
    {
      title: "Dept",
      key: "department",
      width: 150,
      render: (_, row) => {
        if (!canEdit) return <span className="text-xs">{row.department || "-"}</span>;
        return (
          <Select
            size="small"
            className="w-full"
            value={row.department || undefined}
            placeholder="Select..."
            options={DEPARTMENT_OPTIONS}
            allowClear
            onChange={(v) => patchFinding(row.id, { department: v || "" })}
          />
        );
      },
    },
    {
      title: "Type",
      key: "type",
      width: 140,
      render: (_, row) => {
        if (!canEdit) {
          if (!row.type) return <span className="text-xs text-[color:var(--color-text-muted)]">-</span>;
          return (
            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${TYPE_COLORS[row.type] || ""}`}>
              {row.type}
            </span>
          );
        }
        return (
          <Select
            size="small"
            className="w-full"
            value={row.type || undefined}
            placeholder="Select..."
            options={TYPE_OPTIONS}
            allowClear
            onChange={(v) => patchFinding(row.id, { type: v || "" })}
          />
        );
      },
    },
  ], [canEdit, patchFinding, revision]);

  const unresolved = Array.isArray(auditFindings?.unresolvedControlIds) ? auditFindings.unresolvedControlIds : [];

  const unresolvedNotice = unresolved.length > 0 ? (
    <div
      data-testid="unresolved-controls-notice"
      role="alert"
      className="rounded-2xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]"
    >
      <strong>Needs a human decision.</strong> {unresolved.length} control
      {unresolved.length === 1 ? " has" : "s have"} no document in the library, so no finding was generated
      for {unresolved.length === 1 ? "it" : "them"}: {unresolved.join(", ")}.
    </div>
  ) : null;

  if (!auditFindings || localRows.length === 0) {
    return (
      <div className="space-y-4">
        {unresolvedNotice}
        <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-12 shadow-sm">
          <Empty description="Complete all questionnaire questions and attach documents to generate audit findings." />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {unresolvedNotice}
      <div className="rounded-3xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-sm">
        <div className="mb-6">
          <h3 className="text-lg font-black tracking-tight text-[color:var(--color-text-hero)]">Audit Findings</h3>
          <p className="text-xs text-[color:var(--color-text-muted)] mt-1">
            {localRows.length} finding{localRows.length !== 1 ? "s" : ""} generated from questionnaire evidence
          </p>
        </div>

        <Table
          rowKey="id"
          dataSource={localRows}
          columns={columns}
          pagination={false}
          scroll={{ x: 1100 }}
          size="middle"
        />
      </div>

      {canEdit && canProceed && (
        <div className="flex justify-end">
          <Button
            type="primary"
            size="large"
            icon={<ArrowRightOutlined />}
            loading={proceedLoading}
            onClick={handleProceedToCorrectiveActions}
            className="px-8"
          >
            Proceed to Corrective Actions
          </Button>
        </div>
      )}
    </div>
  );
}

export default AuditFindingsSubTab;
